/**
 * Rendering rules for message parts (prompt.md §30, §48).
 *
 * WHY descriptors instead of JSX here: a transcript part can be text, reasoning,
 * an image, a file artifact, a tool call or a tool result, and *all* clients
 * (web, desktop, Android) must agree on how each is summarised. This module
 * turns a provider-neutral `MessagePart` into a small, testable description that
 * a renderer can turn into markup.
 */

import type { MessagePart } from "@dayvise/domain";

export type MessagePartDescriptor =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "reasoning"; readonly text: string }
  | { readonly kind: "image"; readonly url: string; readonly alt: string }
  | { readonly kind: "file"; readonly fileId: string; readonly name: string; readonly mimeType: string }
  | { readonly kind: "toolCall"; readonly toolCallId: string; readonly toolId: string; readonly argumentsText: string }
  | { readonly kind: "toolResult"; readonly toolCallId: string; readonly ok: boolean; readonly summary: string }
  | { readonly kind: "secret"; readonly secretId: string; readonly label: string };

/** Tool ids arrive as `namespace.name` or `namespace/name`. */
export function toolLabel(toolId: string): string {
  const leaf = toolId.split(/[./]/).pop() ?? toolId;
  return leaf
    .split(/[_-]/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Pretty-print tool arguments. Truncated because transcripts scroll. */
export function formatArguments(value: unknown, maxLength = 600): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return truncate(value, maxLength);
  try {
    const json = JSON.stringify(value, null, 2);
    return json === undefined ? "" : truncate(json, maxLength);
  } catch {
    return truncate(String(value), maxLength);
  }
}

export function truncate(text: string, maxLength: number): string {
  return text.length <= maxLength ? text : `${text.slice(0, Math.max(0, maxLength - 1))}…`;
}

/** One-line summary of a tool result, used in the collapsed transcript. */
export function summarizeToolResult(content: unknown, maxLength = 240): string {
  if (content === null || content === undefined) return "no output";
  if (typeof content === "string") return truncate(content.replace(/\s+/g, " ").trim(), maxLength);
  if (typeof content === "number" || typeof content === "boolean") return String(content);
  try {
    const json = JSON.stringify(content);
    return json === undefined ? "no output" : truncate(json, maxLength);
  } catch {
    return truncate(String(content), maxLength);
  }
}

export function describeMessagePart(part: MessagePart): MessagePartDescriptor {
  switch (part.type) {
    case "text":
      return { kind: "text", text: part.text };
    case "reasoning":
      return { kind: "reasoning", text: part.text };
    case "image":
      return { kind: "image", url: part.url, alt: `image (${part.mimeType})` };
    case "file":
      return { kind: "file", fileId: part.fileId, name: part.name, mimeType: part.mimeType };
    case "tool_call":
      return {
        kind: "toolCall",
        toolCallId: part.toolCallId,
        toolId: part.toolId,
        argumentsText: formatArguments(part.arguments),
      };
    case "tool_result":
      return { kind: "toolResult", toolCallId: part.toolCallId, ok: part.ok, summary: summarizeToolResult(part.content) };
    case "secret":
      // Sealed secret: the client holds only the sealed envelope, never the
      // value. Render a lock chip with the label; there is deliberately no
      // reveal path — values resolve server-side inside tool execution only.
      return { kind: "secret", secretId: part.secretId, label: part.label };
    default: {
      const exhausted: never = part;
      throw new TypeError(`unsupported message part: ${JSON.stringify(exhausted)}`);
    }
  }
}

/** Join the readable text of a message, used for previews and search. */
export function messagePlainText(parts: readonly MessagePart[], maxLength = 400): string {
  const text = parts
    .map((part) => (part.type === "text" || part.type === "reasoning" ? part.text : ""))
    .filter((chunk) => chunk.length > 0)
    .join(" ")
    .trim();
  return truncate(text, maxLength);
}

/**
 * The full text of a message, for a copy control.
 *
 * WHY reasoning is excluded and the length is not capped: a copy must reproduce what
 * the message said, not a preview of it, and internal reasoning is not part of what
 * was said on the user's behalf.
 */
export function messageCopyText(parts: readonly MessagePart[]): string {
  const chunks: string[] = [];
  for (const part of parts) if (part.type === "text") chunks.push(part.text);
  return chunks.join("\n").trim();
}

/** Human label for a message author. */
export function messageRoleLabel(role: string): string {
  switch (role) {
    case "user":
      return "You";
    case "assistant":
      return "Agent";
    case "system":
      return "System";
    case "developer":
      return "Developer";
    case "tool":
      return "Tool";
    default:
      return role;
  }
}
